import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
// import Button from './Button';

/*
  Modal Types:
  - Basic
    - default (small, normal, large)
    - centered (small, normal, large)
  - Side
    - right (small, normal, large)
    - left (small, normal, large)
    - top side
    - bottom side
  - Modifications
    - full screen
    - alert
    - transparent backdrop
  - Transparent (tinted)
    - Default (normal, centered)
    - Left & Right (left, right)
    - top
    - bottom
*/
/*
  Modal toggle is controlled by data-target and data-toggle="modal" attributes
  i.e no booleans are required since js controls this behavior as well as closing when click outside of modal

  <Button> elements MUST have a data-toggle="modal" and a data-target attribute of the modal id
  <Modal> element's id value would correspond to the button's data-target

  <Button
    variant="primary"
    data-toggle="modal"
    data-target="#myModal"
  >
  ...
  <Modal
    id="myModal"
  >

*/
/*
  Type: alert, backdrop-transparent, fullscreen, transparent, primary - default is primary
  Size: sm & lg - default is the medium size
  Position: center, left, right, top, bottom - default is the basic modal
*/

const DynamicModal = ({
  children,
  modalHeader,
  modalSubHeader,
  id,
  position,
  onClickDismiss,
  size,
  type,
}) => {
  // Alert, Fullscreen, Backdrop-transparent all affect the outer className
  const generateOuterClassName = () => {
    let currClassName = 'modal fade';
    switch (type) {
      case 'alert':
        currClassName += ' modal-alert';
        break;
      case 'transparent':
        currClassName += ' modal-backdrop-transparent';
        break;
      case 'fullscreen':
        currClassName += ' modal-fullscreen';
        break;
      default:
        currClassName += '';
    }
    return currClassName;
  };

  const generateSizeClass = currClassName => {
    let sizeClass = currClassName;
    switch (size) {
      case 'sm':
        sizeClass += ' modal-sm';
        break;
      case 'lg':
        sizeClass += ' modal-lg';
        break;
      default:
        sizeClass += '';
    }
    return sizeClass;
  };

  const generatePositionClass = currClassName => {
    let positionClass = currClassName;
    switch (position) {
      case 'center':
        positionClass += ' modal-dialog-centered';
        break;
      case 'left':
        positionClass += ' modal-dialog-left';
        break;
      case 'right':
        positionClass += ' modal-dialog-right';
        break;
      case 'top':
        positionClass += ' modal-dialog-top';
        break;
      case 'bottom':
        positionClass += ' modal-dialog-bottom';
        break;
      default:
        positionClass += '';
    }
    return positionClass;
  };

  const generateInnerClassName = () => {
    let currClassName = 'modal-dialog';
    switch (type) {
      case 'alert':
      case 'fullscreen':
        currClassName += ' modal-dialog-centered';
        break;
      case 'backdropTransparent':
        currClassName += ' modal-lg modal-dialog-centered';
        break;
      case 'transparent':
        currClassName += ' modal-transparent';
        currClassName = generateSizeClass(currClassName);
        currClassName = generatePositionClass(currClassName);
        break;
      case 'primary':
      default:
        currClassName = generateSizeClass(currClassName);
        currClassName = generatePositionClass(currClassName);
    }
    return currClassName;
  };

  // close modal on navigation away
  useEffect(() => () => document.querySelectorAll('.modal-backdrop')?.forEach(el => el?.remove?.()), []);
  return (
    <div
      className={generateOuterClassName()}
      tabIndex="-1"
      id={id}
      role="dialog"
    >
      <div className={generateInnerClassName()} role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className={type === 'transparent' ? 'modal-title text-white' : 'modal-title'}>
              {modalHeader}
              {
                modalSubHeader && <small className={type === 'transparent' ? 'm-0 text-white opacity-70' : 'm-0 text-muted'}>{modalSubHeader}</small>
              }
            </h4>
            <a
              className={type === 'transparent' ? 'close text-white' : 'close'}
              data-dismiss="modal"
              onClick={onClickDismiss}
            >
              <span aria-hidden="true">
                <i className="fal fa-times" />
              </span>
            </a>
          </div>
          <div className={type === 'transparent' ? 'modal-body text-white' : 'modal-body'}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

DynamicModal.propTypes = {
  children: PropTypes.node.isRequired,
  modalHeader: PropTypes.string,
  modalSubHeader: PropTypes.string,
  id: PropTypes.string,
  onClickDismiss: PropTypes.func,
  position: PropTypes.string,
  size: PropTypes.string,
  type: PropTypes.string,
};

DynamicModal.defaultProps = {
  modalHeader: null,
  modalSubHeader: null,
  position: null,
  size: null,
  type: null,
};

export default DynamicModal;
